import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { UserRole } from '../../shared/models/user.model';
import {
  CreateManagementUserRequest,
  DmeOrganization,
  ManagementUser,
  ServiceCenter,
} from '../../shared/models/user-management.model';

export interface ManagementUserQuery {
  role?: string;
  dmeOrgCode?: string | null;
  serviceCenterCode?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class UserManagementService {
  private readonly getAllManagementUsersUrl = `${environment.apiUrl}/User/GetAllManagementUsers`;
  private readonly getAllDmeOrgsUrl = `${environment.apiUrl}/DmeOrg/GetAllDmeOrgs`;
  private readonly getAllServiceCentersUrl = `${environment.apiUrl}/ServiceCenter/GetAllServiceCenters`;
  private readonly createUserUrl = `${environment.apiUrl}/User/CreateUser`;
  private readonly deactivateUserUrl = `${environment.apiUrl}/User/DeleteUserByUserCode`;
  private readonly http = inject(HttpClient);

  getAllManagementUsers(query: ManagementUserQuery): Observable<{ users: ManagementUser[]; total: number }> {
    const params = this.buildParams(query);
    return this.http.get<any>(this.getAllManagementUsersUrl, { params }).pipe(
      map((response) => {
        const data = response?.data;
        if (Array.isArray(data)) {
          return { users: data as ManagementUser[], total: data.length };
        }
        if (Array.isArray(data?.users)) {
          return { users: data.users as ManagementUser[], total: Number(data.totalCount ?? data.users.length) };
        }
        return { users: [], total: 0 };
      }),
    );
  }

  getAllDmeOrgs(): Observable<DmeOrganization[]> {
    return this.http.get<any>(this.getAllDmeOrgsUrl).pipe(
      map((response) => {
        const organizations = this.extractList<DmeOrganization>(response);
        const loggedInDmeOrgCode = this.getLoggedInDmeOrgCodeFromStorage();

        if (!loggedInDmeOrgCode) {
          return organizations;
        }

        return organizations.filter((org) => org?.dmeOrgCode === loggedInDmeOrgCode);
      }),
    );
  }

  getAllServiceCenters(dmeOrgCode: string): Observable<ServiceCenter[]> {
    const params = new HttpParams().set('dmeOrgCode', dmeOrgCode || '');
    return this.http.get<any>(this.getAllServiceCentersUrl, { params }).pipe(
      map((response) => this.extractList<ServiceCenter>(response)),
    );
  }

  createUser(payload: CreateManagementUserRequest): Observable<any> {
    return this.http.post<any>(this.createUserUrl, payload);
  }

  deactivateUserByUserCode(userCode: string): Observable<any> {
    const payload = {
      loggedInUserCode: this.getLoggedInUserCodeFromStorage(),
      userCode: String(userCode || '').trim(),
    };
    return this.http.post<any>(this.deactivateUserUrl, payload);
  }

  private buildParams(query: ManagementUserQuery): HttpParams {
    const normalizedRole = this.normalizeRole(query.role);
    const dmeOrgCode = query.dmeOrgCode ?? '';
    const serviceCenterCode = query.serviceCenterCode ?? '';

    // Super/App admin: both blank
    if (normalizedRole === this.normalizeRole(UserRole.Super_Admin) || normalizedRole === this.normalizeRole(UserRole.App_Admin)) {
      return new HttpParams()
        .set('DmeOrgCode', '')
        .set('ServiceCenterCode', '');
    }

    // DME admin: only DmeOrgCode
    if (normalizedRole === this.normalizeRole(UserRole.DME_Admin)) {
      return new HttpParams()
        .set('DmeOrgCode', dmeOrgCode)
        .set('ServiceCenterCode', '');
    }

    // DME user: both DmeOrgCode and ServiceCenterCode
    if (normalizedRole === this.normalizeRole(UserRole.DME_User)) {
      return new HttpParams()
        .set('DmeOrgCode', dmeOrgCode)
        .set('ServiceCenterCode', serviceCenterCode);
    }

    // Safe fallback for any other role
    return new HttpParams()
      .set('DmeOrgCode', dmeOrgCode)
      .set('ServiceCenterCode', serviceCenterCode);
  }

  private normalizeRole(role?: string): string {
    return (role || '').toLowerCase().replace(/\s+/g, '_');
  }

  private extractList<T>(response: any): T[] {
    const data = response?.data;
    if (Array.isArray(data)) {
      return data as T[];
    }
    if (Array.isArray(data?.items)) {
      return data.items as T[];
    }
    if (Array.isArray(response)) {
      return response as T[];
    }
    return [];
  }

  private getLoggedInDmeOrgCodeFromStorage(): string {
    try {
      const raw = localStorage.getItem('currentUser');
      if (!raw) {
        return '';
      }

      const user = JSON.parse(raw) as any;
      return String(user?.dmeOrgDetails?.dmeOrgCode || user?.dmeOrgCode || '').trim();
    } catch {
      return '';
    }
  }

  private getLoggedInUserCodeFromStorage(): string {
    try {
      const raw = localStorage.getItem('currentUser');
      if (!raw) {
        return '';
      }

      const user = JSON.parse(raw) as any;
      return String(user?.userCode || '').trim();
    } catch {
      return '';
    }
  }
}
